import React, { useRef } from 'react';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { get, isFunction } from 'lodash';
import { useSnackbar } from 'notistack';

import { useUploadFileMutation } from 'app/apis/upload.api';
import { useConfig } from 'app/config.ctx';
import { joinURL } from 'utils/helpers';
import FormTextField from './textfield';

const FormFileUpload = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { name, disabled, valueExtend, editableFilename, getLabel, valueOnRemove, ...restProps } = props;

  const { t } = useTranslation();
  const uploadInputRef = useRef(null);
  const [uploadFile] = useUploadFileMutation();
  const { BASE_URL } = useConfig();

  return (
    <Controller
      name={name}
      render={({ field }) => {
        const docUri = get(field, 'value.uri');
        const onUpload = (e) => {
          let selectedFile = e.target.files[0];
          const formData = new FormData();
          formData.append('file', selectedFile);
          formData.append('uploadType', 'file');

          uploadFile(formData).unwrap().then((resp) => {
            if (isFunction(valueExtend)) {
              field.onChange({
                ...get(field, 'value', {}),
                ...valueExtend(resp, selectedFile),
              });
            } else {
              field.onChange(resp.temporaryLocation.replace('/temp/', '/private/temp/'));
            }
          });
        }
        const onCopyLinkToDashboard = (link) => {
          navigator.clipboard.writeText(joinURL(BASE_URL, link));
          enqueueSnackbar('File link copied to clipboard', { variant: 'success' });
        };

        const onRemoveFile = () => {
          if (isFunction(valueOnRemove)) {
            field.onChange(valueOnRemove(field.value));
          } else {
            field.onChange(null);
          }
        };

        if (docUri) {
          const docLabel = isFunction(getLabel) ? getLabel(field) : 'download';

          let docDownloadUrl = `/api/download/?uri=${docUri}`;
          if (docLabel && docLabel.indexOf('.') > -1) {
            docDownloadUrl += `&name=${docLabel}`;
          }

          if (editableFilename) {
            return (
              <Grid container alignItems="center">
                <Grid item>
                  <a href={docDownloadUrl} target={"_blank"}>
                    <FileDownloadIcon fontSize="small"/>
                  </a>
                </Grid>
                <Grid item sx={{ flexGrow: 1 }}>
                  <FormTextField
                    fullWidth
                    size="small"
                    name={`${name}.${editableFilename}`}
                  />
                </Grid>
                <Grid item>
                  <DeleteOutlineIcon
                    fontSize="small"
                    onClick={onRemoveFile}
                    sx={{ cursor: 'pointer' }}
                  />
                </Grid>
              </Grid>
            )
          }

          return (
            <Box>
              <a href={docDownloadUrl} target={"_blank"} style={{ fontSize: 14 }}>
                {docLabel}
              </a>
              <Box>
                <DeleteOutlineIcon sx={{ fontSize: 14, cursor: "pointer" }} onClick={onRemoveFile}/>
                <ContentCopyIcon sx={{ fontSize: 14, cursor: "pointer" }}
                                 onClick={e => onCopyLinkToDashboard(docDownloadUrl)}/>
              </Box>
            </Box>
          );
        }

        return (
          <>
            <a
              onClick={() => uploadInputRef.current.click()}
              style={{ cursor: "pointer" }}
            >
              select file
              <AddIcon sx={{ fontSize: 14 }}/>
            </a>
            <input
              type='file'
              onChange={onUpload}
              style={{ display: 'none' }}
              accept='.doc,.docx,.pdf,.xml,.html,.htm'
              ref={uploadInputRef}
            />
          </>
        );
      }}
    />
  )
};

export default FormFileUpload;
